import type { ObjectTag } from '@atlas/types'
import { ClassifiedObjectType } from '@atlas/types'
import { useState } from 'react'
import type { Selection } from '@/components/classification/ChipSet'
import { AddTupleForm } from '@/components/library/AddTupleForm'
import { ClassificationPanel } from '@/components/library/ClassificationPanel'
import { tagsToChipTuple, tagsToChipTuples } from '@/domain/asset-detail.types'
import { useClassify, useRemoveTuple } from '@/hooks/useClassification'
import { useAxesWithValues } from '@/hooks/useLibrary'
import { useCan } from '@/lib/capabilities'

export interface FileClassificationSectionProps {
	fileId: string
	tags: ObjectTag[]
}

/**
 * A file's classification panel, wired to the write path.
 *
 * Split out of `AssetDetailPage` because it is the page's one stateful concern:
 * it owns the add/remove mutations, the compose-form toggle, and the role gate,
 * none of which the surrounding page reads. Keeping it here also keeps the page
 * under the complexity guard, which the inlined version breached.
 *
 * Classifying is open to USER and above — the same gate the write routes
 * enforce. A VIEWER still reads the panel but is offered no controls, rather
 * than getting a live button that 403s; the backend stays authoritative either
 * way, this only avoids showing an affordance that cannot work.
 */
export function FileClassificationSection({ fileId, tags }: FileClassificationSectionProps) {
	const { axes } = useAxesWithValues()
	const { canWork } = useCan()
	const classify = useClassify()
	const removeTuple = useRemoveTuple()
	const [isAdding, setIsAdding] = useState(false)

	// Undefined handlers are how the panel knows to withhold an affordance.
	const writeHandlers = canWork
		? {
				onAddTuple: () => setIsAdding(true),
				onRemoveTuple: (tupleId: number) =>
					removeTuple.mutate({ objectType: ClassifiedObjectType.FILE, objectId: fileId, tupleId }),
			}
		: {}

	/**
	 * Commit the picked selection as a new assertion. The tuple id is allocated
	 * SERVER-SIDE (see `useClassify`), never computed here from the rendered
	 * tuples — a client-computed "next" ordinal races two concurrent adds (or one
	 * stale tab) into landing in the same tuple instead of getting one each.
	 */
	const classifyFile = (selection: Selection) => {
		if (Object.keys(selection).length === 0) return
		classify.mutate(
			{ objectType: ClassifiedObjectType.FILE, objectId: fileId, tuple: selection },
			{ onSuccess: () => setIsAdding(false) },
		)
	}

	return (
		<ClassificationPanel
			tuple={tagsToChipTuple(tags)}
			// The file's assertions kept apart — one group per tuple.
			tuples={tagsToChipTuples(tags)}
			{...writeHandlers}
			removeError={removeTuple.isError ? 'Could not remove the assertion — please try again.' : null}
			addForm={
				isAdding ? (
					<AddTupleForm
						axes={axes}
						onSubmit={classifyFile}
						onCancel={() => setIsAdding(false)}
						isSaving={classify.isPending}
						error={classify.isError ? 'Could not add the assertion — please try again.' : null}
					/>
				) : null
			}
		/>
	)
}
